import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Donation } from '@/hooks/useDonations';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatAmount } from '@/utils/formatAmount';

interface HighscoreTimerProps {
  highestDonation: Donation | null;
}

interface TimeUnit {
  count: number;
  key: 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
}

const getTimeElapsed = (createdAt: string): TimeUnit => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now.getTime() - created.getTime();
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years >= 1) return { count: years, key: 'year' };
  if (months >= 1) return { count: months, key: 'month' };
  if (weeks >= 1) return { count: weeks, key: 'week' };
  if (days >= 1) return { count: days, key: 'day' };
  if (hours >= 1) return { count: hours, key: 'hour' };
  if (minutes >= 1) return { count: minutes, key: 'minute' };
  return { count: seconds, key: 'second' };
};

const HighscoreTimer = ({ highestDonation }: HighscoreTimerProps) => {
  const { t } = useTranslation();
  const { displayCurrency, convert } = useCurrency();
  const [timeElapsed, setTimeElapsed] = useState<TimeUnit | null>(null);

  useEffect(() => {
    if (!highestDonation) return;

    setTimeElapsed(getTimeElapsed(highestDonation.created_at));

    const interval = setInterval(() => {
      setTimeElapsed(getTimeElapsed(highestDonation.created_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [highestDonation]);

  if (!highestDonation) {
    return (
      <div className="w-full max-w-md mx-auto mt-6 sm:mt-8 terminal-border p-4 sm:p-6 text-center">
        <p className="text-muted-foreground text-xs sm:text-sm">
          {t('highscore.waitingForFirst')}
        </p>
      </div>
    );
  }

  const timeString = timeElapsed 
    ? t(`highscore.time.${timeElapsed.key}`, { count: timeElapsed.count })
    : '';

  return (
    <div className="w-full max-w-md mx-auto mt-6 sm:mt-8 terminal-border p-4 sm:p-6">
      {/* Titel */}
      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 text-center">
        👑 {t('highscore.currentRecord')} 👑
      </p>
      
      {/* Name - prominent */}
      <p className="text-xl sm:text-2xl md:text-3xl font-bold terminal-glow text-center mb-2">
        {highestDonation.display_name}
      </p>
      
      {/* Betrag - in Rot */}
      <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-destructive text-center mb-4">
        {formatAmount(convert(Number(highestDonation.amount), highestDonation.currency), displayCurrency)}
      </p>
      
      {/* Nachricht - das Bragging Right */}
      {highestDonation.message && (
        <p className="text-sm sm:text-base md:text-lg text-muted-foreground italic text-center mb-4">
          "{highestDonation.message}"
        </p>
      )}
      
      {/* Timer */}
      <div className="text-center border-t border-border pt-3 mt-3">
        <span className="text-muted-foreground">⏱ </span>
        <span className="text-sm sm:text-base glitch-hover terminal-glow">
          {t('highscore.unbeatenFor', { time: timeString.toUpperCase() })}
        </span>
      </div>
    </div>
  );
};

export default HighscoreTimer;
